import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Upload, ChartLineUp, Lightbulb, Target, Bell, TrendUp, ListChecks, ChartPie, ArrowRight } from "phosphor-react";
import { updateSeoMetadata } from "../lib/seo";

// Logo URLs
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";
const LOGO_BRAIN_GENI = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";

export default function Landing() {
  useEffect(() => {
    updateSeoMetadata({
      title: "GENI — Turn trading data into better decisions",
      description: "GENI is a trading analytics platform for crypto traders. Import your trades from Binance, Bybit, OKX, BingX and get automated insights to improve your edge.",
      path: "/",
      lang: "en",
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      
      {/* Navigation */}
      <nav className="w-full px-6 py-5 flex items-center justify-between max-w-[1200px] mx-auto">
        {/* Brand - Horizontal Logo */}
        <div className="flex items-center flex-shrink-0">
          <img 
            src={LOGO_HORIZONTAL} 
            alt="GENI"
            className="h-10 md:h-11 lg:h-12 w-auto object-contain"
          />
        </div>
        <div className="flex items-center gap-4">
          <Link 
            to="/login" 
            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            Login
          </Link>
          <Link 
            to="/signup" 
            className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-sm hover:bg-white/90 transition-colors"
          >
            Start tracking
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-6 pt-16 pb-24 max-w-[1200px] mx-auto">
        <div className="flex flex-col items-center justify-center text-center">
          {/* Logo - Main Visual Anchor - LARGE */}
          <div className="flex justify-center mb-10 md:mb-12">
            <div className="w-[220px] sm:w-[280px] md:w-[360px] lg:w-[400px] xl:w-[440px]">
              <img
                src={LOGO_BRAIN_GENI}
                alt="GENI Logo"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Stop guessing.<br />
            <span className="text-white/80">Start understanding your trading.</span>
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-white/60 max-w-[600px] mb-8 leading-relaxed">
            Built by a trader, for traders. Geni helps you track, analyze, and improve 
            your trading performance with real data.
          </p>

          {/* CTA Buttons */}
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link 
              to="/signup" 
              className="px-8 py-3 bg-[#00E676] text-black font-bold rounded-sm hover:bg-[#00E676]/90 transition-colors flex items-center gap-2"
            >
              Start tracking <ArrowRight size={18} weight="bold" />
            </Link>
            <Link 
              to="/login" 
              className="px-8 py-3 border border-white/20 text-white font-semibold rounded-sm hover:bg-white/5 transition-colors"
            >
              Login
            </Link>
          </div>
        </div>
      </section>

      {/* Features - 3 Columns */}
      <section className="px-6 py-20 bg-white/[0.02]">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-xl bg-[#0A0A0A] border border-white/10">
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-lg flex items-center justify-center mb-5">
                <Upload size={24} className="text-[#00E676]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Track your trades effortlessly</h3>
              <p className="text-white/60 leading-relaxed">
                Upload a chart and let Geni extract the data. No more manual entry or messy spreadsheets.
              </p>
            </div>
            <div className="p-8 rounded-xl bg-[#0A0A0A] border border-white/10">
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-lg flex items-center justify-center mb-5">
                <ChartLineUp size={24} className="text-[#00E676]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Behavior Alarms</h3>
              <p className="text-white/60 leading-relaxed">
                Get alerted when you break your own rules, overtrade, or deviate from your edge.
              </p>
            </div>
            <div className="p-8 rounded-xl bg-[#0A0A0A] border border-white/10">
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-lg flex items-center justify-center mb-5">
                <Lightbulb size={24} className="text-[#00E676]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Know what actually works</h3>
              <p className="text-white/60 leading-relaxed">
                Clear insights from real performance data — so you can double down on what works and cut what doesn't.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-24 max-w-[1200px] mx-auto">
        <h2 className="text-3xl md:text-4xl font-black text-center mb-16">How it works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
              <span className="text-2xl font-black text-[#00E676]">1</span>
            </div>
            <h3 className="text-lg font-bold mb-2">Upload your chart</h3>
            <p className="text-white/50 text-sm">
              Paste or upload a screenshot of your trade
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
              <span className="text-2xl font-black text-[#00E676]">2</span>
            </div>
            <h3 className="text-lg font-bold mb-2">Complete your trade</h3>
            <p className="text-white/50 text-sm">
              Review extracted data and add your notes
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
              <span className="text-2xl font-black text-[#00E676]">3</span>
            </div>
            <h3 className="text-lg font-bold mb-2">Get insights and improve</h3>
            <p className="text-white/50 text-sm">
              See patterns, track progress, and refine your edge
            </p>
          </div>
        </div>
      </section>

      {/* Differentiation */}
      <section className="px-6 py-20 bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-2xl md:text-3xl font-bold leading-relaxed">
            This is not a spreadsheet.<br />
            <span className="text-white/60">This is a system built to evolve your trading.</span>
          </p>
        </div>
      </section>

      {/* Features List */}
      <section className="px-6 py-24 max-w-[1200px] mx-auto">
        <h2 className="text-3xl md:text-4xl font-black text-center mb-16">Everything you need</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <Lightbulb size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Insights</h4>
              <p className="text-white/50 text-sm">Spot patterns in your trading behavior</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <Target size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Planned trades tracking</h4>
              <p className="text-white/50 text-sm">Track trades you cancel and understand why</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <ChartPie size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Partial execution analysis</h4>
              <p className="text-white/50 text-sm">See how partials affect your results</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <Bell size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Alerts</h4>
              <p className="text-white/50 text-sm">Stay disciplined with custom risk rules</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <TrendUp size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Performance tracking</h4>
              <p className="text-white/50 text-sm">Track your capital growth over time</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-5 rounded-lg bg-white/[0.02] border border-white/5">
            <ListChecks size={22} className="text-[#00E676] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Setup & timeframe analysis</h4>
              <p className="text-white/50 text-sm">Know which setups and times work best</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-transparent to-[#00E676]/5">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-6">
            Start improving your trading today.
          </h2>
          <p className="text-white/60 mb-10">
            Join traders who are taking control of their performance data.
          </p>
          <Link 
            to="/signup" 
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#00E676] text-black font-bold text-lg rounded-sm hover:bg-[#00E676]/90 transition-colors"
          >
            Create account <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer - Strong Branding Section */}
      <footer className="px-6 py-16 md:py-20 border-t border-white/10">
        <div className="max-w-[1200px] mx-auto flex flex-col items-center gap-10">
          {/* Large Logo */}
          <div className="flex items-center justify-center">
            <img 
              src={LOGO_HORIZONTAL} 
              alt="GENI"
              className="h-12 md:h-14 lg:h-16 w-auto object-contain mx-auto"
            />
          </div>
          
          {/* Navigation Links */}
          <div className="flex items-center gap-8 text-sm text-white/50">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
            <Link to="/login" className="hover:text-white transition-colors">Login</Link>
          </div>
          
          {/* Tagline */}
          <div className="text-sm text-white/30">
            Built by a trader, for traders.
          </div>
        </div>
      </footer>
      
    </div>
  );
}
